import { emberEquipmentKey } from "game-common/ember_sprite/generate"
import { Frame } from "game-common/ember_sprite/raster"
import { Appearance, Entity, Inventory } from "game-common/models"
import { BLEND_MODES, Container, InteractionEvent, Rectangle, Sprite, Text, Texture } from "pixi.js"

import { PixelLayer } from "../../intro/pixel_layer"
import { ART_PX, PixelBuffer, bayer, rgb } from "../../intro/pixel_raster"
import { titleLetter, trackingFor } from "../../intro/pixel_wordmark"
import { disposeTextTextures } from "../../text/dispose_text"
import { CreationLayout, StageBounds, creationLayout, figureBox, pageBounds } from "./creation_layout"
import { Choice, CreationSheet } from "./creation_sheet"
import { DeckArt, DeckKind, FLAME_FRAMES, paintDeck, paintLantern, lightPools } from "./deck_art"
import { ditherFigures, groundRowOf, lightFigure, stampFigure } from "./figure_art"
import { NightSky } from "./night_sky"
import { Hit, PanelAction, TextLine, choiceId, hitAt, paintPanel } from "./panel_art"
import { Pictures, PictureTrait, RenderFrame, pictureSpec, renderIdle } from "./pictures"
import { capsText, fitText, text } from "./widgets"

// The character-creation page: the login's night over the ship's rail, the character on the deck under
// the stern lantern, and one panel of every trait. Everything is pixel art on the intro's grid (one art
// pixel is ART_PX logical pixels); only the words are type.
//
// Built once when it opens. A pick or a pointer move repaints only the layers it changes: the figure
// while it dithers to its new look, the panel when what it shows changes, the lantern's flame a few times
// a second. At rest almost nothing is uploaded.

export interface CreationPageHost {
    submitCharacter(appearance: Appearance, inventory: Inventory): void
    close(): void
}

export interface KeyboardSource {
    addListener(listener: (key: string, down: boolean) => void): void
    removeListener(listener: (key: string, down: boolean) => void): void
}

export interface CreationPageOptions {
    keyboard?: KeyboardSource
    /** The canvas, for the mouse wheel (pixi 6 never delivers `wheel` to display objects). */
    canvas?: () => HTMLCanvasElement | undefined
    render?: RenderFrame
    pictures?: Pictures
}

/** When each piece arrives after the page opens, in ms, by the intro's ordered dither. */
export const ENTRANCE = {
    lantern: 450,
    figure: [180, 420],
    title: [320, 420],
    lens: [560, 380],
    subtitle: [640, 300],
    panel: [380, 240],
    /** Each art row of the panel starts this much later than the one above it: it dissolves in top down. */
    panelRow: 1.5,
    cta: [700, 250],
    done: 1000,
}

/** When the last piece of the entrance lands, in ms: the panel's bottom row, or the way on after it. */
export const entranceEndsAt = (layout: CreationLayout): number =>
    Math.max(
        ENTRANCE.panel[0] + layout.panel.h * ENTRANCE.panelRow + ENTRANCE.panel[1],
        ENTRANCE.cta[0] + ENTRANCE.cta[1],
        ENTRANCE.figure[0] + ENTRANCE.figure[1],
        ENTRANCE.title[0] + ENTRANCE.title[1],
        ENTRANCE.lens[0] + ENTRANCE.lens[1],
        ENTRANCE.subtitle[0] + ENTRANCE.subtitle[1],
    )
const CHANGE_MS = 150
const TURN_MS = 70
const FLARE_MS = 520
const PING_MS = 420
const ROLL_MS = 420
const EXIT = { panel: 200, dark: [150, 350], close: 520 }
const FLAME_MS = 120

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v)
const easeOut = (t: number) => 1 - Math.pow(1 - clamp01(t), 3)
const steps = (a: number, n: number) => Math.round(clamp01(a) * n) / n
const progress = (ms: number, [from, over]: number[]) => clamp01((ms - from) / over)

const flicker = (ms: number) => 0.84 + 0.08 * Math.sin(ms / 97) + 0.05 * Math.sin(ms / 41 + 1.3) + 0.03 * Math.sin(ms / 13)

const FRAME_CACHE = 64

interface Die {
    face: number
    from: number
    ms: number
}

interface Ember {
    sprite: Sprite
    ms: number
    x: number
    vx: number
    vy: number
    life: number
}

export class CreationPage extends Container {
    readonly sheet: CreationSheet
    private readonly host: CreationPageHost
    private readonly options: CreationPageOptions
    private readonly renderFrame: RenderFrame
    private readonly pictures: Pictures
    private layout?: CreationLayout
    private boundsKey = ""
    /** How much larger than the stage the page is drawn: 1 on desktop, up to 2 on a phone. */
    private pageScale = 1

    // Everything is drawn in stage coordinates, inside this container, which the page offsets so the
    // stage's top-left lands at the viewport's.
    private readonly stage = new Container()
    private readonly night = new NightSky()
    private readonly deckLayer = new PixelLayer(false)
    private readonly lightLayer = new PixelLayer(false)
    private readonly lanternLayer = new PixelLayer(false)
    private readonly figureLayer = new PixelLayer(false)
    private readonly emberLayer = new Container()
    private readonly titleLayer = new PixelLayer(false)
    private readonly panelLayer = new PixelLayer(false)
    private readonly ctaLayer = new PixelLayer(false)
    private readonly turnLayer = new PixelLayer(false)
    private readonly textLayer = new Container()
    private readonly exitLayer = new PixelLayer(false)

    private deck?: DeckArt
    private title?: { body: PixelBuffer; w: number; h: number }
    private titleKey = ""
    private subtitle?: Text

    // The page's clock, and when the entrance started (undefined once it has landed).
    private ms = 0
    private entranceAt?: number = 0
    private exitAt?: number

    // The figure: frames by look, the one shown and the one it is dithering from.
    private frames = new Map<string, Frame>()
    private grounds = new Map<string, number>()
    private figure?: PixelBuffer
    private previous?: PixelBuffer
    private changeAt = -1e9
    private changeMs = CHANGE_MS
    private figureDirty = true

    // The panel's state between repaints.
    private art?: { hits: Hit[]; texts: TextLine[] }
    private panelDirty = true
    private hover?: string
    private lastPicked?: PictureTrait
    private pings: { id: string; ms: number }[] = []
    private dice: { look: Die; outfit: Die } = { look: { face: 5, from: 5, ms: 1e9 }, outfit: { face: 3, from: 3, ms: 1e9 } }
    private texts = new Map<string, Text>()
    private flareAt = -1e9
    private embers: Ember[] = []
    private flame = -1
    private pointerArt?: { x: number; y: number }
    private drag?: { x: number; pointerId: number; moved: boolean }
    private dragged = false
    private wheelListener?: (event: WheelEvent) => void
    private disposed = false

    constructor(player: Entity, host: CreationPageHost, options: CreationPageOptions = {}) {
        super()
        this.sheet = new CreationSheet(player)
        this.host = host
        this.options = options
        this.renderFrame = options.render ?? renderIdle
        this.pictures = options.pictures ?? new Pictures({ render: this.renderFrame })

        this.lightLayer.sprite.blendMode = BLEND_MODES.ADD
        this.stage.addChild(
            this.night,
            this.deckLayer.sprite,
            this.lightLayer.sprite,
            this.lanternLayer.sprite,
            this.figureLayer.sprite,
            this.emberLayer,
            this.titleLayer.sprite,
            this.panelLayer.sprite,
            this.ctaLayer.sprite,
            this.turnLayer.sprite,
            this.textLayer,
            this.exitLayer.sprite,
        )
        this.addChild(this.stage)

        this.stage.interactive = true
        this.stage.on("pointerdown", this.onPointerDown)
        this.stage.on("pointermove", this.onPointerMove)
        this.stage.on("pointerup", this.onPointerUp)
        this.stage.on("pointertap", this.onTap)
        this.stage.on("pointerupoutside", this.onPointerUp)
        this.stage.on("pointerout", () => this.setHover(undefined))
        options.keyboard?.addListener(this.onKey)
        this.attachWheel()
    }

    // ---- building ----------------------------------------------------------------------------

    /**
     * Fit the page to the visible stage, drawn `scale` times larger than it (see pageScale), rebuilding
     * the scene when either changed. A scale above 1 lays the page out for a phone.
     */
    fit = (stageBounds: StageBounds, scale: number = 1) => {
        const key = `${stageBounds.left}|${stageBounds.top}|${stageBounds.right}|${stageBounds.bottom}|${scale}`
        // The page's own coordinates are the stage's divided by the scale; its top-left lands on the viewport's.
        this.stage.scale.set(scale)
        this.stage.position.set(-stageBounds.left, -stageBounds.top)
        if (key === this.boundsKey) {
            return
        }
        this.boundsKey = key
        if (scale !== this.pageScale) {
            this.pageScale = scale
            this.refreshTexts()
        }
        const bounds = pageBounds(stageBounds, scale)
        const layout = creationLayout(bounds, scale > 1)
        this.layout = layout
        this.stage.hitArea = new Rectangle(bounds.left, bounds.top, bounds.right - bounds.left, bounds.bottom - bounds.top)

        this.night.fit(layout)

        const deck = paintDeck(layout)
        this.deck = deck
        this.deckLayer.place(layout.view.x, layout.view.y, layout.view.w, layout.view.h)
        this.deckLayer.commit(deck.buffer)
        const light = lightPools(layout, deck)
        this.lightLayer.place(layout.view.x, layout.view.y, layout.view.w, layout.view.h)
        this.lightLayer.commit(light)

        const box = figureBox(layout)
        this.figureLayer.place(box.x, box.y, box.w, box.h)
        this.figure = undefined
        this.previous = undefined
        this.figureDirty = true

        this.buildTitle(layout)
        this.exitLayer.place(layout.view.x, layout.view.y, layout.view.w, layout.view.h)
        this.exitLayer.sprite.visible = false
        this.panelDirty = true
        this.flame = -1
        this.paint()
    }

    /** "samsara" in the login's own pixel letters, with the lens under it, centred over the figure. */
    private buildTitle = (layout: CreationLayout) => {
        const h = 12
        const letters = [..."samsara"].map(ch => titleLetter(ch, h))
        const track = trackingFor(h)
        const pad = 4
        const inner = letters.reduce((w, l) => w + l.advance, 0) + track * (letters.length - 1)
        const w = inner + pad * 2
        const lensY = h + 2 + pad + 3
        const height = lensY + 3
        const x0 = layout.figX - Math.floor(w / 2)
        const body = new PixelBuffer(w, height, x0, layout.titleY)
        let x = 0
        for (const letter of letters) {
            for (let j = 0; j < letter.glow.h; j++) {
                for (let i = 0; i < letter.glow.w; i++) {
                    const a = letter.glow.data[j * letter.glow.w + i] >>> 24
                    if (a) body.blend(x0 + x + i, layout.titleY + j, rgb(0xffffff), (a / 255) * 0.3)
                }
            }
            for (let j = 0; j < letter.body.h; j++) {
                for (let i = 0; i < letter.body.w; i++) {
                    const c = letter.body.data[j * letter.body.w + i]
                    if (c >>> 24) body.blend(x0 + x + pad + i, layout.titleY + pad + j, c | 0xff000000, (c >>> 24) / 255)
                }
            }
            x += letter.advance + track
        }
        // The lens: brightest in the middle, tapering to nothing.
        const half = 52
        const cx = layout.figX
        for (let k = -half; k <= half; k++) {
            const u = Math.abs(k) / half
            const a = u < 0.18 ? 1 : u < 0.45 ? 0.75 : u < 0.75 ? 0.45 : 0.2
            body.blend(cx + k, layout.titleY + lensY, rgb(u < 0.18 ? 0xffd27a : 0xd9b75c), a)
            if (u < 0.1) body.blend(cx + k, layout.titleY + lensY - 1, rgb(0xd9b75c), 0.35)
        }
        this.title = { body, w, h: height }
        this.titleKey = ""
        this.titleLayer.place(body.x0, body.y0, body.w, body.h)
        if (!this.subtitle) {
            this.subtitle = capsText("Character creation", { size: 9, weight: "500", color: 0xc9b293 })
            this.sharpen(this.subtitle)
            this.subtitle.anchor.set(0.5, 0)
            this.textLayer.addChild(this.subtitle)
        }
        this.subtitle.position.set(layout.figX * ART_PX, (layout.titleY + height) * ART_PX + 4)
    }

    // ---- the figure ----------------------------------------------------------------------------

    private frameFor = (sprite: string, facing: string, equipment: any): Frame => {
        const key = `${sprite}|${facing}|${emberEquipmentKey(equipment)}`
        let frame = this.frames.get(key)
        if (frame) {
            this.frames.delete(key)
        } else {
            frame = this.renderFrame(sprite, facing as any, equipment)
        }
        this.frames.set(key, frame)
        while (this.frames.size > FRAME_CACHE) this.frames.delete(this.frames.keys().next().value)
        return frame
    }

    private groundOf = (sprite: string): number => {
        let ground = this.grounds.get(sprite)
        if (ground === undefined) {
            ground = groundRowOf(this.renderFrame(sprite, "front", { hair: "bald" }))
            this.grounds.set(sprite, ground)
        }
        return ground
    }

    /** The figure as the sheet has it now, stamped on the feet line. */
    private stampCurrent = (hop: number = 0): PixelBuffer => {
        const layout = this.layout
        const sprite = this.sheet.sprite()
        const frame = this.frameFor(sprite, this.sheet.facingName, this.sheet.equipment())
        return stampFigure(figureBox(layout), frame, this.groundOf(sprite), layout.figX, layout.feetY, hop)
    }

    /** The look or the facing changed: dither from what is on screen to the new figure. */
    private refigure = (ms: number) => {
        this.previous = this.figure
        this.figure = this.stampCurrent()
        this.changeAt = this.ms
        this.changeMs = ms
        this.figureDirty = true
    }

    private paintFigure = (reveal: number) => {
        const layout = this.layout
        if (!this.figure) {
            this.figure = this.stampCurrent()
        }
        const k = clamp01((this.ms - this.changeAt) / this.changeMs)
        const changing = k < 1 && !!this.previous
        if (!changing && !this.figureDirty && reveal >= 1) {
            return
        }
        // A change hops the new figure a pixel at its height.
        const hop = changing && this.changeMs >= CHANGE_MS && k > 0.25 && k < 0.75 ? 1 : 0
        const to = hop ? this.stampCurrent(hop) : this.figure
        let shown = changing ? ditherFigures(this.previous, to, k) : to
        if (reveal < 1) {
            shown = ditherFigures(undefined, shown, reveal)
        }
        const deck = this.deck
        const isPlank = (x: number, y: number) => {
            const i = deck.buffer.index(x, y)
            return i >= 0 && deck.kind[i] === DeckKind.Plank
        }
        this.figureLayer.commit(lightFigure(shown, isPlank, layout.figX, layout.feetY))
        this.figureDirty = changing || reveal < 1
        if (!changing) {
            this.previous = undefined
        }
    }

    // ---- the panel -----------------------------------------------------------------------------

    private paintPanel = (reveal: (y: number) => number, ctaReveal: number) => {
        const layout = this.layout
        const sheet = this.sheet
        const dieOf = (die: Die) => {
            const t = die.ms / ROLL_MS
            if (t >= 1) return { face: die.face, hop: 0 }
            return { face: t < 0.75 ? 1 + ((die.from + Math.floor(t * 9)) % 6) : die.face, hop: Math.round(2 * Math.sin(Math.PI * Math.min(1, t * 1.3))) }
        }
        const art = paintPanel(layout, {
            mode: sheet.mode,
            rows: sheet.rows(),
            hairWindow: sheet.hairWindow(),
            gridOptions: sheet.gridOptions(),
            hover: this.hover,
            dice: { look: dieOf(this.dice.look), outfit: dieOf(this.dice.outfit) },
            picture: (trait, value) => this.pictures.get(pictureSpec(sheet.draft, trait, value), trait === this.lastPicked),
            tabs: sheet.tabs,
            hairPage: perPage => sheet.hairPages(perPage),
        })
        this.art = art
        // The tile just picked rings once, outward.
        for (const ping of this.pings) {
            const hit = art.hits.find(h => h.id === ping.id)
            if (!hit) continue
            const t = ping.ms / PING_MS
            const g = 1 + Math.floor(t * 3)
            const alpha = steps(1 - t, 3)
            for (let i = -g; i < hit.w + g; i++) {
                art.panel.blend(hit.x + i, hit.y - g, rgb(0xffd27a), alpha)
                art.panel.blend(hit.x + i, hit.y + hit.h - 1 + g, rgb(0xffd27a), alpha)
            }
            for (let j = -g; j < hit.h + g; j++) {
                art.panel.blend(hit.x - g, hit.y + j, rgb(0xffd27a), alpha)
                art.panel.blend(hit.x + hit.w - 1 + g, hit.y + j, rgb(0xffd27a), alpha)
            }
        }
        const dissolve = (buf: PixelBuffer, at: (y: number) => number) => {
            const out = new PixelBuffer(buf.w, buf.h, buf.x0, buf.y0)
            for (let j = 0; j < buf.h; j++) {
                const r = at(buf.y0 + j)
                if (r <= 0) continue
                for (let i = 0; i < buf.w; i++) {
                    if (r >= 1 || bayer(buf.x0 + i, buf.y0 + j) < r) out.data[j * buf.w + i] = buf.data[j * buf.w + i]
                }
            }
            return out
        }
        this.panelLayer.place(art.panel.x0, art.panel.y0, art.panel.w, art.panel.h)
        this.panelLayer.commit(dissolve(art.panel, reveal))
        this.ctaLayer.place(art.cta.x0, art.cta.y0, art.cta.w, art.cta.h)
        this.ctaLayer.commit(dissolve(art.cta, () => ctaReveal))
        this.turnLayer.place(art.turn.x0, art.turn.y0, art.turn.w, art.turn.h)
        this.turnLayer.commit(dissolve(art.turn, () => ctaReveal))
        this.setTexts(art.texts, reveal, ctaReveal)
    }

    /** The panel's words, reusing each Text while its string and style stay the same. */
    private setTexts = (lines: TextLine[], reveal: (y: number) => number, ctaReveal: number) => {
        const next = new Map<string, Text>()
        const placed = new Map<string, Text>()
        for (const line of lines) {
            const key = `${line.key}|${line.text}|${line.color}|${line.weight}|${line.size}`
            let label = this.texts.get(key)
            if (label) {
                this.texts.delete(key)
            } else {
                const options = { size: line.size, weight: line.weight, color: line.color, display: true }
                label = line.caps ? capsText(line.text, options) : text(line.text, options)
                this.sharpen(label)
                this.textLayer.addChild(label)
            }
            next.set(key, label)
            placed.set(line.key, label)
            label.anchor.set(line.align === "center" ? 0.5 : line.align === "right" ? 1 : 0, line.middle ? 0.5 : 0)
            if (line.follows && placed.has(line.follows)) {
                const before = placed.get(line.follows)
                label.position.set(before.x + before.width + 7, before.y - 1)
            } else {
                label.position.set(line.x, line.y)
            }
            if (line.fitTo !== undefined && label.x + label.width > line.fitTo) {
                fitText(label, Math.max(20, line.fitTo - label.x))
            }
            const inCta = line.key === "journey"
            const r = inCta ? ctaReveal : reveal(label.y / ART_PX)
            label.alpha = steps(r, 4)
            label.visible = r > 0
        }
        // Whatever was not used this time goes.
        this.texts.forEach(label => disposeTextTextures(label))
        this.texts = next
    }

    // ---- input ---------------------------------------------------------------------------------

    private artPoint = (event: InteractionEvent) => {
        const p = event.data.getLocalPosition(this.stage)
        return { x: p.x / ART_PX, y: p.y / ART_PX }
    }

    private onFigure = (p: { x: number; y: number }) => {
        const layout = this.layout
        return !!layout && Math.abs(p.x - layout.figX) < 16 && p.y > layout.feetY - 50 && p.y < layout.feetY + 4
    }

    private onPointerDown = (event: InteractionEvent) => {
        if (!this.layout || this.exitAt !== undefined) return
        const p = this.artPoint(event)
        this.pointerArt = p
        this.landEntrance()
        this.dragged = false
        if (this.art && hitAt(this.art.hits, p.x, p.y)) {
            return
        }
        if (this.onFigure(p)) {
            this.drag = { x: p.x, pointerId: event.data.pointerId, moved: false }
            this.stage.cursor = "grabbing"
        }
    }

    private onPointerMove = (event: InteractionEvent) => {
        if (!this.layout || this.exitAt !== undefined) return
        const p = this.artPoint(event)
        this.pointerArt = p
        if (this.drag && event.data.pointerId === this.drag.pointerId) {
            const turned = this.sheet.drag(p.x - this.drag.x)
            this.drag.x = p.x
            if (turned) {
                this.drag.moved = true
                this.refigure(TURN_MS)
            }
            return
        }
        const hit = this.art && hitAt(this.art.hits, p.x, p.y)
        this.stage.cursor = hit ? "pointer" : this.onFigure(p) ? "grab" : "default"
        // Only a mouse points; a finger's touch is a press.
        if (event.data.pointerType === "mouse") {
            this.setHover(hit)
        }
    }

    private onPointerUp = () => {
        if (this.drag) {
            this.dragged = this.drag.moved
            this.sheet.endDrag()
            this.drag = undefined
            this.stage.cursor = "grab"
        }
    }

    /** A click, or a tap on a phone: whatever control it lands on acts. The end of a drag is not a tap. */
    private onTap = (event: InteractionEvent) => {
        if (!this.layout || this.exitAt !== undefined || this.dragged) {
            this.dragged = false
            return
        }
        const p = this.artPoint(event)
        const hit = this.art && hitAt(this.art.hits, p.x, p.y)
        if (!hit) return
        this.act(hit.action)
        if (hit.action.type === "pick") this.pings.push({ id: hit.id, ms: 0 })
    }

    private setHover = (hit?: Hit) => {
        const id = hit?.id
        if (id === this.hover) return
        this.hover = id
        this.sheet.point(hit?.choice)
        this.panelDirty = true
    }

    private onKey = (key: string, down: boolean) => {
        if (!down || this.exitAt !== undefined || this.disposed) return
        if (key === "ArrowLeft") this.act({ type: "turn", direction: -1 })
        else if (key === "ArrowRight") this.act({ type: "turn", direction: 1 })
        else if (key === "Escape" && this.sheet.mode === "hairGrid") this.act({ type: "closeGrid" })
    }

    private attachWheel = () => {
        const canvas = this.options.canvas?.()
        if (!canvas) return
        this.wheelListener = (event: WheelEvent) => {
            const p = this.pointerArt
            const hit = p && this.art && hitAt(this.art.hits, p.x, p.y)
            if (!hit?.hairRow || this.sheet.mode !== "traits" || this.exitAt !== undefined) return
            event.preventDefault()
            this.act({ type: "hairStep", direction: event.deltaY > 0 ? 1 : -1 })
        }
        canvas.addEventListener("wheel", this.wheelListener, { passive: false })
    }

    /** What every control does. */
    act = (action: PanelAction) => {
        if (this.exitAt !== undefined) return
        this.landEntrance()
        const sheet = this.sheet
        switch (action.type) {
            case "pick":
                if (action.choice.trait === "sex" && sheet.draft.appearance.sex === action.choice.value) return
                sheet.pick(action.choice)
                this.changed(action.choice)
                break
            case "hairStep":
                sheet.stepHair(action.direction)
                this.changed({ trait: "hair", kind: "style", value: sheet.draft.appearance.hair ?? "none" })
                break
            case "openGrid":
                sheet.openGrid()
                this.setHover(undefined)
                break
            case "closeGrid":
                sheet.closeGrid()
                this.setHover(undefined)
                break
            case "rollLook":
                sheet.rollLook()
                this.throwDie(this.dice.look)
                this.changed()
                break
            case "rollOutfit":
                sheet.rollOutfit()
                this.throwDie(this.dice.outfit)
                this.changed()
                break
            case "turn":
                sheet.turn(action.direction)
                this.refigure(TURN_MS)
                break
            case "journey":
                this.leave()
                break
            case "tab":
                sheet.selectTab(action.trait)
                break
            case "hairPage":
                sheet.turnHairPage(action.direction, action.perPage)
                break
        }
        this.panelDirty = true
    }

    /** A pick landed: the figure dithers to it, the lantern flares and throws a few embers. */
    private changed = (choice?: Choice) => {
        this.refigure(CHANGE_MS)
        this.flareAt = this.ms
        this.throwEmbers()
        this.lastPicked = choice && choice.trait !== "sex" && choice.trait !== "skin" ? (choice.trait as PictureTrait) : undefined
        if (choice) this.pings = this.pings.filter(ping => ping.id !== choiceId(choice))
        this.panelDirty = true
    }

    private throwDie = (die: Die) => {
        die.from = die.face
        die.face = 1 + ((die.face + Math.floor(Math.random() * 5)) % 6)
        die.ms = 0
    }

    private throwEmbers = () => {
        for (let k = 0; k < 7; k++) {
            const sprite = new Sprite(Texture.WHITE)
            sprite.width = ART_PX
            sprite.height = ART_PX
            this.emberLayer.addChild(sprite)
            this.embers.push({ sprite, ms: -k * 25, x: this.layout.light.x + (Math.random() * 3 - 1.5), vx: Math.random() * 10 - 5, vy: 18 + Math.random() * 16, life: 500 + Math.random() * 400 })
        }
    }

    private leave = () => {
        this.exitAt = this.ms
        this.landEntrance()
        this.setHover(undefined)
        this.stage.cursor = "default"
        this.host.submitCharacter(this.sheet.getAppearance(), this.sheet.getInventory())
    }

    /** Land everything that is still arriving: the player has already started. */
    private landEntrance = () => {
        if (this.entranceAt !== undefined) {
            this.entranceAt = undefined
            this.panelDirty = true
            this.figureDirty = true
        }
    }

    /** Start the entrance over: the page has just been opened. */
    enter = () => {
        this.entranceAt = this.ms
        this.exitAt = undefined
        this.exitLayer.sprite.visible = false
        this.panelDirty = true
        this.figureDirty = true
    }

    get entering(): boolean {
        return this.entranceAt !== undefined
    }

    // ---- the frame -----------------------------------------------------------------------------

    animate = (dtMs: number) => {
        if (this.disposed || !this.layout) return
        this.ms += dtMs
        this.dice.look.ms += dtMs
        this.dice.outfit.ms += dtMs
        this.pings.forEach(ping => (ping.ms += dtMs))
        this.pings = this.pings.filter(ping => ping.ms < PING_MS)
        if (this.entranceAt !== undefined && this.ms - this.entranceAt > ENTRANCE.done) {
            this.landEntrance()
        }
        if (this.pictures.work() > 0) {
            this.panelDirty = true
        }
        this.paint(dtMs)
        if (this.exitAt !== undefined && this.ms - this.exitAt >= EXIT.close) {
            this.exitAt = undefined
            this.host.close()
        }
    }

    private paint = (dtMs: number = 0) => {
        const layout = this.layout
        if (!layout) return
        const e = this.entranceAt === undefined ? 1e9 : this.ms - this.entranceAt
        const out = this.exitAt === undefined ? 0 : this.ms - this.exitAt

        this.night.animate(this.ms)

        // The lantern: lit first, a flame that steps through its shapes, light that breathes and flares.
        const lit = steps(e / ENTRANCE.lantern, 4)
        const flame = lit > 0 ? Math.floor(this.ms / FLAME_MS) % FLAME_FRAMES : -2
        if (flame !== this.flame) {
            this.flame = flame
            const lantern = paintLantern(Math.max(0, flame), lit > 0)
            this.lanternLayer.place(layout.lanternX, layout.lanternBase - lantern.h + 1, lantern.w, lantern.h)
            this.lanternLayer.commit(lantern)
        }
        const flare = 1 - easeOut((this.ms - this.flareAt) / FLARE_MS)
        this.lightLayer.sprite.alpha = Math.min(1, ((flicker(this.ms) + 0.55 * Math.max(0, flare)) * lit) / 1.4)
        this.paintEmbers(dtMs)

        this.paintFigure(progress(e, ENTRANCE.figure))

        const titleReveal = progress(e, ENTRANCE.title) * (1 - clamp01(out / EXIT.panel))
        const lens = easeOut(progress(e, ENTRANCE.lens))
        const titleKey = `${titleReveal}|${lens}|${out > 0 ? Math.min(out, EXIT.panel) : 0}`
        if (this.title && titleKey !== this.titleKey) {
            this.titleKey = titleKey
            const t = this.title
            const cx = layout.figX
            const shown = new PixelBuffer(t.body.w, t.body.h, t.body.x0, t.body.y0)
            for (let j = 0; j < t.body.h; j++) {
                for (let i = 0; i < t.body.w; i++) {
                    const x = t.body.x0 + i
                    const y = t.body.y0 + j
                    const c = t.body.data[j * t.body.w + i]
                    if (!c) continue
                    const isLens = j >= t.h - 4
                    const r = isLens ? (Math.abs(x - cx) <= 52 * lens ? 1 - clamp01(out / EXIT.panel) : 0) : titleReveal
                    if (r >= 1 || bayer(x, y) < r) shown.data[j * t.body.w + i] = c
                }
            }
            this.titleLayer.commit(shown)
        }
        if (this.subtitle) {
            this.subtitle.alpha = steps(progress(e, ENTRANCE.subtitle) * (1 - clamp01(out / EXIT.panel)), 4)
        }

        const rolling = this.dice.look.ms < ROLL_MS || this.dice.outfit.ms < ROLL_MS
        const arriving = e < ENTRANCE.done
        if (this.panelDirty || rolling || this.pings.length || arriving || out > 0) {
            const leaving = 1 - clamp01(out / EXIT.panel)
            const reveal = (y: number) => Math.min(leaving, clamp01((e - ENTRANCE.panel[0] - (y - layout.panel.y) * ENTRANCE.panelRow) / ENTRANCE.panel[1]))
            this.paintPanel(reveal, Math.min(leaving, progress(e, ENTRANCE.cta)))
            this.panelDirty = false
        }

        if (out > 0) {
            // Leaving: the night goes out a dither step at a time.
            const k = progress(out, EXIT.dark)
            const view = layout.view
            const dark = new PixelBuffer(view.w, view.h, view.x, view.y)
            const ink = rgb(0x05060a)
            for (let j = 0; j < view.h; j++) {
                for (let i = 0; i < view.w; i++) {
                    if (bayer(view.x + i, view.y + j) < k) dark.data[j * view.w + i] = ink
                }
            }
            this.exitLayer.sprite.visible = true
            this.exitLayer.commit(dark)
        }
    }

    private paintEmbers = (dtMs: number) => {
        const layout = this.layout
        this.embers = this.embers.filter(ember => {
            ember.ms += dtMs
            if (ember.ms > ember.life) {
                ember.sprite.destroy()
                return false
            }
            const s = Math.max(0, ember.ms) / 1000
            const fade = 1 - Math.max(0, ember.ms) / ember.life
            ember.sprite.visible = ember.ms >= 0
            ember.sprite.position.set(
                Math.round(ember.x + ember.vx * s + Math.sin(ember.ms / 90) * 1.2) * ART_PX,
                Math.round(layout.lanternBase - 14 - ember.vy * s) * ART_PX,
            )
            ember.sprite.tint = fade > 0.6 ? 0xfff0b8 : fade > 0.3 ? 0xffc56b : 0xb8742e
            return true
        })
    }

    /** A Text in a page drawn larger than the stage is rasterized larger too, so its words stay sharp. */
    private sharpen = (label: Text) => {
        if (this.pageScale > 1) {
            label.resolution = (label.resolution || 1) * this.pageScale
        }
    }

    /** The Text font arrived after the words were set, or the page's scale changed: set them again. */
    refreshTexts = () => {
        this.texts.forEach(label => disposeTextTextures(label))
        this.texts.clear()
        if (this.subtitle) {
            disposeTextTextures(this.subtitle)
            this.subtitle = undefined
            if (this.layout) this.buildTitle(this.layout)
        }
        this.panelDirty = true
    }

    dispose = () => {
        if (this.disposed) return
        this.disposed = true
        this.options.keyboard?.removeListener(this.onKey)
        const canvas = this.options.canvas?.()
        if (canvas && this.wheelListener) canvas.removeEventListener("wheel", this.wheelListener)
        this.pictures.clear()
        this.frames.clear()
        disposeTextTextures(this)
        this.night.dispose()
        ;[this.deckLayer, this.lightLayer, this.lanternLayer, this.figureLayer, this.titleLayer, this.panelLayer, this.ctaLayer, this.turnLayer, this.exitLayer].forEach(layer => layer.dispose())
        this.destroy({ children: true })
    }

    /** For tests and the builder: the controls on screen now, in art pixels. */
    get hits(): Hit[] {
        return this.art?.hits ?? []
    }
}
